/**
 * Created by Ning Jiang on 10/10/2018.
 */

import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {SpinEvent} from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {BitmapWinCountUp} from "@nolimitcity/slot-game/bin/game/winpresentation/countup/BitmapWinCountUp";
import {Elastic, Power2, Power3, Timeline, TimelineLite, TweenLite} from "gsap";
import {DeadwoodSoundConfig} from "../../DeadwoodSoundConfig";
import {DeadwoodGameConfig} from "../../DeadwoodGameConfig";

export class DeadwoodInitialWinCountUp extends BitmapWinCountUp {

    private _isCountUpSoundPlaying:boolean = false;
    private _hasCountUpEndSoundPlayed:boolean = false;
    private _hasMultiplierEndSoundPlayed:boolean = false;
    private _isShowing:boolean = false;
    private _endValue:number;
    private _endMultiplier:number;
    private _countUpComplete:boolean;
    private _isTinyWin:boolean;

    private _isIntro:boolean;

    public set isTinyWin(value:boolean) {
        this._isTinyWin = value;
    }

    constructor(intro?:boolean) {
        super({
            font : {name : "DeadwoodCountUpFont", size : 150},
            tint : parseInt(("#ffffff").replace(/^#/, ''), 16)
        });
        this._isIntro = intro === true;
        EventHandler.addEventListener(this, SpinEvent.BEFORE_START, (event:GameEvent) => this.onBeforeSpinStart());
    }

    protected initAnimations():void {
        super.initAnimations();
        if(this._isIntro) {
            this._countUp.maxTextWidth = GameConfig.instance.REEL_AREA_WIDTH * 0.6;
        }
    }

    private getScale(multiplier:number):number {
        return 1 + (multiplier - 1) * 0.1;
    }

    public getCountUpAnimation(from:number, to:number, duration:number, multiplier:number, winBelowStake:boolean):Timeline {
        this._endValue = to;
        this._endMultiplier = multiplier;
        const oriNumber:number = multiplier === 1 ? this._endValue : MathHelper.floorToDecimals(to / multiplier, 2);
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this._countUp.maxTextWidth = GameConfig.instance.REEL_AREA_WIDTH * 0.7 / this.getScale(multiplier);
                this._countUp.alpha = 1;
                this._countUp.visible = true;
                this._countUpContainer.scale.set(1, 1);
                if(duration > 0.01) {
                    this._isCountUpSoundPlaying = true;
                    this.loopCountUpSound();
                }
                this._isShowing = true;
            },
            this._countUp.getCountUpAnimation(from, oriNumber, {duration : duration, ease : Power3.easeInOut})
        ]);
        if(!winBelowStake) { // #369
            timeline.add(() => this.stopCountUpSound());
        }


        if(multiplier > 1) {
            const levelTime:number = 0.5 * multiplier > 5 ? MathHelper.roundToDecimals(5 / multiplier, 2) : 0.5;
            for(let m:number = 1; m <= multiplier; m++) {
                const showNumber:number = oriNumber * m;
                const scale:number = this.getScale(m);
                timeline.add([
                    () => {
                        if(!winBelowStake) { // #369
                            if(m === multiplier) {
                                SlotGame.sound.playEffect(DeadwoodSoundConfig.instance.COUNT_UP_MULTIPLIER_END);
                                this._hasMultiplierEndSoundPlayed = true;
                            } else {
                                SlotGame.sound.playEffect(DeadwoodSoundConfig.instance.COUNT_UP_MULTIPLIER);
                            }
                        }
                    },
                    this._countUp.getCountUpAnimation(showNumber, showNumber, {duration : 0.01}),
                    TweenLite.to(this._countUpContainer.scale, levelTime / 2,
                        {
                            x : scale,
                            y : scale,
                            ease : Elastic.easeOut.config(2, 1)
                        }),
                ]);
                timeline.add(TweenLite.to(this, levelTime / 2, {}));
            }
        }

        return timeline;
    }

    private loopCountUpSound():void {
        if(!this._isCountUpSoundPlaying) {
            return;
        }

        SlotGame.sound.playEffect(DeadwoodSoundConfig.instance.COUNT_UP).then(
            (data:string) => {
                if(data == DeadwoodSoundConfig.instance.COUNT_UP) {
                    this.loopCountUpSound();
                }
            }
        );
    }

    public abort(endValue:number, isAborted:boolean, multiplier:number, isBigWin:boolean, winBelowStake:boolean):Timeline | null {
        if(isBigWin) {
            return null;
        }

        if(this._countUpComplete) {
            return null;
        }

        this._endValue = endValue;
        this._endMultiplier = multiplier;
        const timeline:TimelineLite = new TimelineLite();
        if(isAborted) {
            timeline.add(this.getAbortAnimation(endValue, multiplier));
            timeline.add(this.stay(winBelowStake)); // #369
        }

        return timeline;
    }

    protected getAbortAnimation(endValue:number, multiplier:number):Timeline {
        this._isShowing = true;
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this._countUp.maxTextWidth = GameConfig.instance.REEL_AREA_WIDTH * 0.7 / this.getScale(multiplier);
                this._countUp.alpha = 1;
                this._countUp.visible = true;
                this._countUpContainer.scale.set(1, 1);
            },
            this._countUp.getCountUpAnimation(endValue, endValue, {duration : 0.01})
        ]);
        if(multiplier > 1) {
            const scale:number = this.getScale(multiplier);
            timeline.add(TweenLite.to(this._countUpContainer.scale, 0.2, {
                x : scale,
                y : scale,
                ease : Elastic.easeOut.config(2, 1)
            }), 0)
        }

        return timeline;
    }

    public getEndingAnimation(endValue:number):Timeline {
        this._isShowing = false;
        const timeline:TimelineLite = new TimelineLite();
        timeline.add(this._countUp.getCountUpAnimation(endValue, endValue, {duration : 0.01}));
        timeline.add([
            TweenLite.to(this._countUp, 0.5, {alpha : 0, ease : Power2.easeIn}),
            TweenLite.to(this._countUpContainer, 0.5, {y : -100, ease : Power2.easeIn})
        ], `+=0.2`); // stay a while after count up.
        timeline.add(() => {
            this._countUpContainer.y = 0;
            this._countUpComplete = false;
            this._isCountUpSoundPlaying = false;
            this._hasCountUpEndSoundPlayed = false;
            this._hasMultiplierEndSoundPlayed = false;
        });

        return timeline;
    }

    public stay(winBelowStake:boolean):Timeline {
        this._countUpComplete = true;
        this.stopCountUpSound();
        const timeline:TimelineLite = new TimelineLite();
        timeline.add(() => {
            if(this._endMultiplier > 1 && !this._hasMultiplierEndSoundPlayed && !winBelowStake) {
                SlotGame.sound.playEffect(DeadwoodSoundConfig.instance.COUNT_UP_MULTIPLIER_END);
                this._hasMultiplierEndSoundPlayed = true;
                this._hasCountUpEndSoundPlayed = true;
            }
        });
        // TODO: timeline.add(()=> EventHandler.dispatchEvent(new GameEvent(SoundEvent.PLAY_SOUND_EFFECT, TombstoneSoundConfig.instance.COUNT_UP_BACK)));
        timeline.add(TweenLite.to(this._countUpContainer.scale, 0.9, {
            x : 0.7,
            y : 0.7,
            ease : Elastic.easeOut.config(2, 0.75)
        }));
        return timeline;
    }

    private onBeforeSpinStart():void {
        if(this._isShowing) {
            this.getEndingAnimation(this._endValue);
        }
    }

    private stopCountUpSound():void {
        if(this._isCountUpSoundPlaying) {
            SlotGame.sound.stopEffect(DeadwoodSoundConfig.instance.COUNT_UP);
            this._isCountUpSoundPlaying = false;
        }

        if(!this._hasCountUpEndSoundPlayed && !this._isTinyWin && this._endMultiplier === 1) {
            SlotGame.sound.playEffect(DeadwoodSoundConfig.instance.COUNT_UP_ENDS);
            this._hasCountUpEndSoundPlayed = true;
        }
    }
}